import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBlog } from "../useHook";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import Loader from "../../../Utils/loader";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (
    typeof window !== "undefined" &&
    window.location?.protocol === "https:" &&
    normalized.startsWith("http://")
  ) {
    normalized = "https:" + normalized.slice(5);
  }
  try {
    return encodeURI(decodeURI(normalized));
  } catch {
    return normalized;
  }
};

const BlogView = () => {
  const { id } = useParams();
  const { getBlogById, addOnHeaderBlog, addPopularBlog } = useBlog();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const { hasPermission } = usePermission();
  const canPopular = hasPermission(SystemPermissions.Blogs.ADD_TO_MOST_POPULAR);
  const canFeatured = hasPermission(SystemPermissions.Blogs.ADD_FEATURED_BLOG);
  const [showPopularModal, setShowPopularModal] = useState(false);
  const [showHeaderModal, setShowHeaderModal] = useState(false);

  const fetchBlog = async () => {
    try {
      const blogData = await getBlogById(id);
      setBlog(blogData);
    } catch (error) {
      console.error("Error fetching blog:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch blog details",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const handlePopularToggle = async () => {
    try {
      const response = await addPopularBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isPopular: !prev.isPopular }));
        setShowPopularModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isPopular
            ? "Blog removed from popular"
            : "Blog added to popular",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling popular status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling popular status",
        icon: "error",
      });
    }
  };

  const handleHeaderToggle = async () => {
    try {
      const response = await addOnHeaderBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isHeader: !prev.isHeader }));
        setShowHeaderModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isHeader
            ? "Blog removed from header"
            : "Blog added to header",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling header status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling header status",
        icon: "error",
      });
    }
  };

  if (loading) {
    return <Loader message="Loading blog details..." />;
  }

  if (!blog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Blog not found
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gray-100 p-4 flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Blog Details</h2>
        <div className="flex flex-wrap gap-3">
          {canPopular && (
            <button
              type="button"
              onClick={() => setShowPopularModal(true)}
              className={`btn justify-center ${
                blog.isPopular ? "btn-delete text-white" : "btn-primary"
              }`}
            >
              {blog.isPopular ? "Remove from Popular" : "Add to Most Popular"}
            </button>
          )}
          {canFeatured && (
            <button
              type="button"
              onClick={() => setShowHeaderModal(true)}
              className={`btn justify-center ${
                blog.isHeader ? "btn-delete text-white" : "btn-primary"
              }`}
            >
              {blog.isHeader
                ? "Remove from Featured Blog"
                : "Add Featured Blog"}
            </button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 pb-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                blog.status === "published"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {blog.status === "published" ? "Published" : "Draft"}
            </span>
            <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
              Category: {blog.category?.name || "Uncategorized"}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {blog.headerImage && (
            <div className="w-full relative min-h-[280px] bg-gray-100">
              <img
                src={normalizeImageUrl(blog.headerImage)}
                alt={blog.name}
                className="w-full h-full min-h-[280px] object-cover object-center"
              />
            </div>
          )}

          <div className="p-8">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {blog.name}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {blog.description}
              </p>
            </div>

            <div className="prose max-w-none">
              {blog.content?.map((block, index) => (
                <div key={index} className="mb-8">
                  {block.type === "text" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: block.content }}
                      className="blog-content text-gray-700 leading-relaxed"
                    />
                  ) : (
                    <div className="flex justify-center">
                      <img
                        src={normalizeImageUrl(block.content)}
                        alt={`Blog content ${index + 1}`}
                        className="max-w-full rounded-lg shadow-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showPopularModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isPopular
                ? "Are you sure you want to remove this blog from popular?"
                : "Are you sure you want to add this blog to popular?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowPopularModal(false)}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePopularToggle}
                className={`btn justify-center ${
                  blog.isPopular ? "btn-delete text-white" : "btn-primary"
                }`}
              >
                {blog.isPopular ? "Remove" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHeaderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isHeader
                ? "Are you sure you want to remove this blog from featured blog?"
                : "Are you sure you want to show this blog on featured blog?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowHeaderModal(false)}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleHeaderToggle}
                className={`btn justify-center ${
                  blog.isHeader ? "btn-delete text-white" : "btn-primary"
                }`}
              >
                {blog.isHeader ? "Remove" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogView;
